import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Search, RotateCcw, Calendar, CheckCircle2, XCircle, Clock, Tablet } from 'lucide-react';
import CustomLoader from './CustomLoader';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';

interface LoginLogItem {
  id: number;
  user_id: number | null;
  username: string;
  ip_address: string;
  status: string;
  detail: string;
  platform: string;
  created_at: string;
  branch_username?: string;
  agent_username?: string;
}

const LoginReport: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<LoginLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [ipSearch, setIpSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Default time range helpers
  const formatDateTimeLocal = (date: Date) => {
    const pad = (num: number) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const getTodayStart = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return formatDateTimeLocal(d);
  };

  const getTodayEnd = () => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return formatDateTimeLocal(d);
  };

  const [startDate, setStartDate] = useState<string>(getTodayStart());
  const [endDate, setEndDate] = useState<string>(getTodayEnd());

  const toBackendFormat = (str: string) => {
    if (!str) return '';
    return str.replace('T', ' ') + ':00';
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/login-logs', {
        params: {
          page,
          limit,
          search: searchTerm.trim() || undefined,
          loginDetail: statusFilter || undefined,
          platform: platformFilter || undefined,
          ip: ipSearch.trim() || undefined,
          startDate: startDate ? toBackendFormat(startDate) : undefined,
          endDate: endDate ? toBackendFormat(endDate) : undefined,
        },
      });

      if (res.data.status) {
        setLogs(res.data.data);
        setTotalPages(res.data.pagination.totalPages);
        setTotalCount(res.data.pagination.totalRecords);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch login logs.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, limit, statusFilter, platformFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const handleReset = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPlatformFilter('');
    setIpSearch('');
    setStartDate(getTodayStart());
    setEndDate(getTodayEnd());
    setPage(1);
  };

  const setQuickSearch = (type: string) => {
    const now = new Date();
    let start = new Date();
    let end = new Date();

    switch (type) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        start.setDate(now.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end.setDate(now.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case 'thisWeek': {
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      }
      case 'lastWeek':
        start.setDate(now.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'thisMonth':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'lastMonth':
        start.setMonth(now.getMonth() - 1);
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        return;
    }

    setStartDate(formatDateTimeLocal(start));
    setEndDate(formatDateTimeLocal(end));
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Login Report</h1>
          <p className="text-sm text-gray-500">
            Home / Login Report
          </p>
        </div>
      </div>

      {/* Filters Form */}
      <form onSubmit={handleSearchSubmit} className="bg-white p-6 rounded-xl border space-y-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* From Date */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">From Date</label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Login Detail Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Login Detail</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="success">Login successful</option>
              <option value="failed">Login failed</option>
            </select>
          </div>

          {/* Platform Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Platform</label>
            <select
              value={platformFilter}
              onChange={(e) => {
                setPlatformFilter(e.target.value);
                setPage(1);
              }}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="Mobile Web">Mobile Web</option>
              <option value="Desktop Web">Desktop Web</option>
            </select>
          </div>

          {/* To Date */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">To Date</label>
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Username Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Username</label>
            <input
              type="text"
              placeholder="Username"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* IP Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">IP</label>
            <input
              type="text"
              placeholder="IP Address"
              value={ipSearch}
              onChange={(e) => setIpSearch(e.target.value)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Quick Search */}
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block">Quick Search</label>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Last Month', value: 'lastMonth' },
              { label: 'Last Week', value: 'lastWeek' },
              { label: 'Yesterday', value: 'yesterday' },
              { label: 'Today', value: 'today' },
              { label: 'This Week', value: 'thisWeek' },
              { label: 'This Month', value: 'thisMonth' },
            ].map((btn) => (
              <button
                key={btn.value}
                type="button"
                onClick={() => setQuickSearch(btn.value)}
                className="px-4 py-2 bg-[#17a2b8] hover:bg-[#138496] text-white rounded font-semibold text-sm transition"
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-start gap-2 pt-2">
          <button
            type="submit"
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold text-sm transition flex items-center gap-1.5 shadow-sm"
          >
            <Search className="w-4 h-4" />
            Search
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-5 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded font-semibold text-sm transition flex items-center gap-1.5 shadow-sm"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </form>

      {/* Show entries dropdown & Info block */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>Show</span>
        <select
          value={limit}
          onChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(1);
          }}
          className="rounded border border-gray-300 px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span>entries</span>
      </div>

      {/* Main Table */}
      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm truncate">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                S.No
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Last Login Date
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Username
              </th>
              {user?.role === "admin" && (
                <>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Agent
                  </th>
                </>
              )}
              {user?.role === "backoffice" && (
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Agent
                </th>
              )}
              {[
                'Login IP',
                'Detail',
                'Platform',
              ].map((h) => (
                <th key={h} className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={6 + (user?.role === "admin" ? 2 : user?.role === "backoffice" ? 1 : 0)} className="text-center py-12">
                  <div className="flex justify-center items-center">
                    <CustomLoader />
                  </div>
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={6 + (user?.role === "admin" ? 2 : user?.role === "backoffice" ? 1 : 0)} className="text-center py-12 text-gray-500 font-semibold">
                  No login activities found.
                </td>
              </tr>
            ) : (
              logs.map((log, i) => (
                <tr key={log.id} className="hover:bg-gray-50/50 transition">
                  <td className="px-6 py-4 text-gray-900 font-medium">
                    {(page - 1) * limit + i + 1}
                  </td>
                  <td className="px-6 py-4 text-gray-600 font-medium">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-gray-900 font-bold">
                    {log.user_id ? `(${log.user_id}) ` : ""}{log.username}
                  </td>
                  {user?.role === "admin" && (
                    <>
                      <td className="px-6 py-4 text-gray-700 font-medium">
                        {log.branch_username || "—"}
                      </td>
                      <td className="px-6 py-4 text-gray-700 font-medium">
                        {log.agent_username || "—"}
                      </td>
                    </>
                  )}
                  {user?.role === "backoffice" && (
                    <td className="px-6 py-4 text-gray-700 font-medium">
                      {log.agent_username || "—"}
                    </td>
                  )}
                  <td className="px-6 py-4 text-gray-700 font-medium">
                    {log.ip_address}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold border ${
                        log.status === 'success'
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-100'
                          : 'bg-rose-50 text-rose-800 border-rose-100'
                      }`}
                    >
                      {log.status === 'success' ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-rose-600" />
                      )}
                      {log.detail}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600 font-medium">
                    {log.platform}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-end gap-2 mt-4">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-4 py-2 border rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition text-sm font-semibold disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-4 py-2 text-sm text-gray-600">
            Page {page} of {totalPages} ({totalCount} total)
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-2 border rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition text-sm font-semibold disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default LoginReport;
